import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { CreateBranch, CreateStaffMember, StaffMember } from "@dentacore/shared-types";

import { createBranch } from "../api/branches";
import { readClinic } from "../api/clinic";
import { createStaff, listStaff } from "../api/staff";
import { BranchForm } from "./branch-form";
import { ClinicSection } from "./clinic-section";
import { GooglePanel } from "./google-panel";
import { PaneHead } from "./pane";
import { PeopleSection } from "./people-section";
import { useSettingsScope } from "./settings-layout";
import styles from "./settings.module.css";
import { Toast } from "./toast";
import { describe, useWriter } from "./use-writer";

/**
 * The four clinic-wide Settings panes.
 *
 * These were the four headings of the old single-page Settings, stacked down
 * one 3,119px column. They are the same four things; what changed is that each
 * now has its own route and its own URL, so "the page where I change the
 * clinic's name" is a link somebody can keep.
 *
 * **Nothing here is branch-scoped.** A clinic has one name, one logo, one
 * subdomain and one set of people, shared across every location — which is why
 * these four sit above the switcher in the rail and ignore it entirely.
 */

/** The clinic's own identity and defaults. */
export function PracticePane(): React.JSX.Element {
  return (
    <>
      <PaneHead title="Practice details" />
      <ClinicSection />
    </>
  );
}

/**
 * The locations, and opening a new one.
 *
 * **The list is here and its settings are not.** Opening hours, chairs,
 * procedures and block reasons used to live inside this list, two clicks down,
 * which is why nobody could find them; they are now their own panes, scoped by
 * the rail's switcher. What is left on a location's own page is what belongs to
 * the location itself rather than to the work done there: its timezone, its
 * how to reach it, and closing it.
 */
export function LocationsPane(): React.JSX.Element {
  const { branches, reloadBranches } = useSettingsScope();
  const { busy, toast, dismiss, run } = useWriter();
  const [opening, setOpening] = useState(false);

  /**
   * The clinic's default timezone, read only to pre-fill the new-location form.
   *
   * A failure here is deliberately silent: it costs a pre-filled field and
   * nothing else, and a toast saying the clinic could not be read would be
   * alarming out of proportion to what was lost.
   */
  const [defaultTimezone, setDefaultTimezone] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    readClinic(controller.signal)
      .then((clinic) => {
        if (!controller.signal.aborted) setDefaultTimezone(clinic.defaultTimezone);
      })
      .catch(() => {});

    return () => controller.abort();
  }, []);

  return (
    <>
      <PaneHead title="Locations" />

      <section className={styles.card}>
        <div className={styles.cardHead}>
          <div>
            <h3 className={styles.cardTitle}>
              {branches === null
                ? "Reading…"
                : `${String(branches.length)} ${branches.length === 1 ? "location" : "locations"}`}
            </h3>
          </div>
          {!opening && branches !== null && (
            <button
              type="button"
              className={styles.primary}
              disabled={busy}
              onClick={() => setOpening(true)}
              data-testid="branch-open"
            >
              Open a location
            </button>
          )}
        </div>

        {opening && (
          <div className={styles.cardBody}>
            <h4 className={styles.panelTitle}>A new location</h4>
            <BranchForm
              branch={null}
              busy={busy}
              defaultTimezone={defaultTimezone}
              onCancel={() => setOpening(false)}
              onSubmit={(body) => {
                void run(
                  () => createBranch(body as CreateBranch),
                  reloadBranches,
                  "Location opened.",
                ).then((created) => {
                  // Closed on the write landing and on nothing else — the same
                  // correction every other form here took on 2026-09-06.
                  if (created) setOpening(false);
                });
              }}
            />
          </div>
        )}

        {branches === null ? (
          <div className={styles.cardBody}>
            <p className={styles.quiet}>Reading&hellip;</p>
          </div>
        ) : branches.length === 0 ? (
          <div className={styles.cardBody}>
            <p className={styles.blank} data-testid="no-branches">
              No locations yet. The first one you open becomes bookable as soon as it has a chair.
            </p>
          </div>
        ) : (
          <ul className={`${styles.cardRows} ${styles.listCompact}`}>
            {branches.map((branch) => (
              <li key={branch.id}>
                {/*
                  The whole row is the link. A row clickable only on its name is
                  the kind of target people miss, and this is the one action the
                  pane exists to offer.
                */}
                <Link
                  to={`/settings/locations/${branch.id}`}
                  className={styles.row}
                  data-testid="branch-row"
                >
                  <span className={styles.rowBody}>
                    <span className={styles.rowTitle}>
                      <span className={styles.rowName} data-testid="branch-name">
                        {branch.name}
                      </span>
                      {!branch.isActive && (
                        <span className={styles.tagQuiet} data-testid="branch-closed">
                          Closed
                        </span>
                      )}
                      {!branch.isMember && (
                        <span className={styles.tagQuiet} data-testid="branch-not-member">
                          Not rostered here
                        </span>
                      )}
                    </span>
                    <span className={styles.rowMeta}>
                      {branch.timezone}
                      {branch.address !== null && branch.address !== "" && (
                        <> &middot; {branch.address}</>
                      )}
                    </span>
                  </span>
                  <span className={styles.chevron} aria-hidden="true">
                    ›
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </>
  );
}

/**
 * Everyone who can sign in.
 *
 * **This is the pane the old index was mostly made of.** With the seeded demo
 * clinic it rendered 31 rows with no search, no filter and no paging, which was
 * roughly 80% of a 3,119px page — and it pushed Google Calendar into the gap
 * between the locations and the staff, where nobody looked. Searching and
 * filtering it is Story C; giving it a route of its own is this one.
 */
export function PeoplePane(): React.JSX.Element {
  const { branches } = useSettingsScope();
  const { busy, toast, dismiss, fail, run } = useWriter();
  const [staff, setStaff] = useState<readonly StaffMember[] | null>(null);

  /**
   * The link just issued, held here rather than in the section.
   *
   * **It exists in this one place and nowhere else** — the server keeps only a
   * hash — so it must survive the section re-rendering after the list reloads,
   * and it must not survive a navigation.
   */
  const [invite, setInvite] = useState<{
    name: string;
    link: string | null;
    emailed: boolean;
  } | null>(null);

  const reloadStaff = useCallback(async (signal?: AbortSignal) => {
    setStaff(await listStaff(signal));
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    reloadStaff(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setStaff([]);
      fail(describe(cause, "Could not read this clinic's people."));
    });

    return () => controller.abort();
  }, [reloadStaff, fail]);

  return (
    <>
      <PaneHead title="Staff" />

      <PeopleSection
        staff={staff}
        branches={branches ?? []}
        busy={busy}
        invite={invite}
        onCreate={(body: CreateStaffMember) =>
          // Returned rather than fired-and-forgotten: the panel stays open when
          // this resolves false, so a refusal no longer discards what was typed.
          run(
            async () => {
              const created = await createStaff(body);
              if (created.invite !== undefined) {
                setInvite({
                  name: `${created.firstName} ${created.lastName}`,
                  // **The server's link, verbatim** (QA20-5). This used to be
                  // assembled from `window.location.origin`, which is the apex
                  // whenever an admin runs their clinic from it — and a link to
                  // the apex is refused by the accept page.
                  link: created.invite.acceptUrl,
                  emailed: created.invite.emailed,
                });
              }
            },
            reloadStaff,
            // Deliberately not "we emailed them" — this fires before the
            // response is read, and the panel below says which actually
            // happened (QA20-4).
            "Added.",
          )
        }
        onDismissInvite={() => setInvite(null)}
      />

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </>
  );
}

/** The Google Calendar mirror, one calendar per chair. */
export function GooglePane(): React.JSX.Element {
  return (
    <>
      <PaneHead title="Google Calendar" />
      <GooglePanel />
    </>
  );
}
